import { Alert, ScrollArea, Skeleton } from '@ibsys/kit';
import { IconButton } from '@/shared/ui';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { KnowledgeNodeInSet } from '@/entities/knowledge-set';
import { SmartSearchInput, useSmartSearchPaginated } from '@/features/smart-search';
import { SelectedNodeItem } from './SelectedNodeItem';

const PAGE_SIZE = 50;

type Props = {
  knowledgeSetId?: number;
  isCreating: boolean;
  localNodeIds: Set<number>;
  /** Состав сета на сервере: по нему отделяются ещё не сохранённые добавления */
  savedNodeIds: Set<number>;
  nodesCache: Map<number, KnowledgeNodeInSet>;
  onRemoveNode: (nodeId: number) => void;
  readonly?: boolean;
};

export const SelectedNodesTab: FC<Props> = ({
  knowledgeSetId,
  isCreating,
  localNodeIds,
  savedNodeIds,
  nodesCache,
  onRemoveNode,
  readonly,
}) => {
  const { t } = useTranslation(['knowledgeSets', 'common']);

  // Узлы сохранённого сета листает сервер; о составе создаваемого сета он ещё не знает
  const isServerPaginated = !isCreating && !!knowledgeSetId;

  const baseFilters = useMemo(
    () => ({
      ...(isServerPaginated ? { knowledgeSetId } : {}),
      isDeleted: false,
    }),
    [isServerPaginated, knowledgeSetId],
  );

  const {
    searchState,
    committed,
    items: serverItems,
    page,
    setPage,
    totalPages: serverTotalPages,
    predicateInfo,
    handleSearch,
    handleClear,
    isLoading,
    isSearchAnimating,
    searchError,
  } = useSmartSearchPaginated<'knowledge', KnowledgeNodeInSet>({
    entityType: 'knowledge',
    baseFilters,
    pageSize: PAGE_SIZE,
    enabled: isServerPaginated,
  });

  // Отмеченные, но ещё не сохранённые узлы: в выдаче сета сервер их не вернёт
  const unsavedNodes = useMemo(() => {
    const nodes: KnowledgeNodeInSet[] = [];
    for (const nodeId of localNodeIds) {
      if (savedNodeIds.has(nodeId)) continue;
      const cached = nodesCache.get(nodeId);
      if (cached) nodes.push(cached);
    }
    return nodes;
  }, [localNodeIds, savedNodeIds, nodesCache]);

  // Узлы сета с текущего серверного листа; снятые до сохранения из него убираются
  const savedOnPage = useMemo(
    () => (isServerPaginated ? serverItems.filter((node) => localNodeIds.has(node.id)) : []),
    [isServerPaginated, serverItems, localNodeIds],
  );

  // Листается только состав сета на сервере: несохранённые добавления сервер не нумерует и в total не считает.
  // Поэтому они идут отдельной группой над листом, а не внутри него — иначе лист был бы длиннее объявленного счётчиком
  const pageItems = useMemo(
    () => (isServerPaginated ? savedOnPage : unsavedNodes.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)),
    [isServerPaginated, savedOnPage, unsavedNodes, page],
  );

  const totalPages = isServerPaginated ? serverTotalPages : Math.ceil(unsavedNodes.length / PAGE_SIZE);

  // Набор мог сократиться (снятые узлы, смена сета) — уводим с листа, которого больше нет
  useEffect(() => {
    if (page > 0 && page >= totalPages) {
      setPage(totalPages > 0 ? totalPages - 1 : 0);
      return;
    }
    // Снятие узлов серверные листы не укорачивает: лист, все узлы которого сняты, приходит пустым
    // и выдаёт непустой сет за пустой. Отступаем к началу, пока лист не окажется с узлами
    if (!isLoading && pageItems.length === 0 && localNodeIds.size > 0 && page > 0) setPage(page - 1);
  }, [page, totalPages, setPage, isLoading, pageItems.length, localNodeIds.size]);

  if (localNodeIds.size === 0 && !isLoading) {
    return <Alert className="text-muted-foreground p-4" title={t('knowledgeSets:noSelectedNodes')} />;
  }

  if (isLoading && serverItems.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-12" />
        ))}
      </div>
    );
  }

  const isSearchApplied = isServerPaginated && !!committed;
  // Несохранённые добавления показываются и под применённым условием: до сохранения сервер о них не знает
  // и отобрать их по условию не может, поэтому группа подписана как не подпадающая под поиск
  const showUnsavedNodes = isServerPaginated && unsavedNodes.length > 0;
  // Пустой лист под применённым условием — «по условию ничего не найдено», а не «в сете нет нод»;
  // при непустой группе несохранённых пустой лист сета вовсе не выдаётся за пустой список
  const showEmptyAlert = pageItems.length === 0 && !isLoading && (isSearchApplied || !showUnsavedNodes);

  return (
    <div className="flex flex-col gap-4 h-full overflow-hidden">
      {isServerPaginated && (
        <SmartSearchInput
          value={searchState.value}
          onChange={searchState.onChange}
          mode={searchState.searchMode}
          onModeChange={searchState.setSearchMode}
          onSearch={handleSearch}
          onClear={handleClear}
          predicateInfo={predicateInfo}
          error={searchError}
          isLoading={isSearchAnimating}
          placeholder={t('knowledgeSets:searchNodesPlaceholder')}
        />
      )}

      <ScrollArea className="flex-1">
        <div className="flex flex-col gap-4">
          {showUnsavedNodes && (
            <section className="flex flex-col gap-2">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-muted-foreground">{t('knowledgeSets:unsavedNodes')}</span>
                {isSearchApplied && (
                  <span className="text-xs text-muted-foreground">{t('knowledgeSets:unsavedNodesNotSearched')}</span>
                )}
              </div>
              <ul className="flex flex-col gap-2">
                {unsavedNodes.map((node) => (
                  <SelectedNodeItem key={node.id} node={node} onRemove={onRemoveNode} readonly={readonly} />
                ))}
              </ul>
            </section>
          )}

          <section className="flex flex-col gap-2">
            {showUnsavedNodes && (
              <span className="text-xs font-medium text-muted-foreground">{t('knowledgeSets:savedNodes')}</span>
            )}
            {showEmptyAlert ? (
              <Alert
                className="text-muted-foreground p-4"
                title={isSearchApplied ? t('knowledgeSets:noNodesFoundBySearch') : t('knowledgeSets:noSelectedNodes')}
              />
            ) : (
              <ul className="flex flex-col gap-2">
                {pageItems.map((node) => (
                  <SelectedNodeItem key={node.id} node={node} onRemove={onRemoveNode} readonly={readonly} />
                ))}
              </ul>
            )}
          </section>
        </div>
      </ScrollArea>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 py-2">
          <IconButton
            variant="outline"
            size="icon"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            label={t('common:dataGrid.prevPage')}
            icon={<ChevronLeftIcon className="size-4" />}
          />
          <span className="text-sm text-muted-foreground">{`${page + 1} / ${totalPages}`}</span>
          <IconButton
            variant="outline"
            size="icon"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            label={t('common:dataGrid.nextPage')}
            icon={<ChevronRightIcon className="size-4" />}
          />
        </div>
      )}
    </div>
  );
};
